import axios from "axios";
import Vue from "vue";
import i18n from "@/plugins/i18n";
import { DateTime } from "luxon";
import utils from "../../utils";
import defs from "../../modules";

const codeDefs = {
  identifications: {
    url: "/v1/all/code/identifications",
    name: "listIdentifications"
  },
  vehicleTypes: {
    url: "/v1/all/code/vehicle-types",
    name: "listVehicleTypes"
  },
  vehicleCategories: {
    url: "/v1/all/code/vehicle-categories",
    name: "listVehicleCategories"
  },
  vehicleNorms: {
    url: "/v1/all/code/vehicle-norms",
    name: "listVehicleNorms"
  },
  vehicleMotionDetections: {
    url: "/v1/all/code/vehicle-motion-detections",
    name: "listVehicleMotionDetections"
  },
  deviceTypes: {
    url: "/v1/all/code/device-types",
    name: "listDeviceTypes"
  },
  vehicleTankTypes: {
    url: "/v1/all/code/vehicle-tank-types",
    name: "listVehicleTankTypes"
  },
  serviceTypes: {
    url: "/v1/all/code/service-types",
    name: "listServiceTypes"
  },
  tankCalcMethods: {
    url: "/v1/all/code/tank-calc-methods",
    name: "listTankCalcMethods"
  },
  fuelTypes: {
    url: "/v1/all/code/fuel-types",
    name: "listFuelTypes"
  },
  currencies: {
    url: "/v1/all/code/currencies",
    name: "listCurrencies"
  },
  rideTypes: {
    url: "/v1/all/code/ride-types",
    name: "listRideTypes"
  },
  parameters: {
    url: "/v1/all/code/parameters",
    name: "listParameters"
  },
  countries: {
    url: "/v1/all/code/countries",
    name: "listCountries"
  }
};

const state = {
  /**
   * Vybarný modul dat (vehicle, person, company)
   */
  module: null,
  /**
   * Devifinice modulu
   */
  def: null,
  /**
   * Vybraný měsíc
   */
  month: DateTime.now().toFormat("yyyy-M"),
  /**
   * Rychlý filtr
   */
  fastFilter: null,
  /**
   * Aktálně zobrazené záznamy.
   */
  records: [],

  /**
   * Zobrazený záznam (v pohledu nebo editaci).
   */
  record: null,
  /**
   * Edituje se záznam
   */
  edit: false,
  /**
   * Zásobních, neuloženách objektů. Struktura:
   * {
   *   module: "..."
   *   id: "..."
   *   record: ...
   * }
   */
  stack: [],

  /**
   * Fronta událostí.
   * Fronta je pro oba směry (container, nebo view)
   * formát:
   * {
   *   target: 'container' nebo 'view'
   *   ...
   * }
   */
  events: [],
  activeEvent: null,

  fetching: false,
  fetched: false,
  running: null,

  /* číselníky */
  // identifikace
  listIdentifications: [],
  // typy vozidel
  listVehicleTypes: [],
  // kategorie vozidel
  listVehicleCategories: [],
  // EU normy
  listVehicleNorms: [],
  // Detekce pohybu
  listVehicleMotionDetections: [],
  // Detekce pohybu
  listDeviceTypes: [],
  // Typy nadrží
  listVehicleTankTypes: [],
  // Typy servisů
  listServiceTypes: [],
  // Metody počítání stavu nádrže
  listTankCalcMethods: [],
  // Typ paliva
  listFuelTypes: [],
  // Měna
  listCurrencies: [],
  // typy jizd
  listRideTypes: [],
  // Typy parametrů
  listParameters: [],
  // Státy
  listCountries: []
};

const getters = {
  getRecords: (state) => {
    return state.records;
  },

  getRecord: (state) => {
    return state.record;
  },

  getCodes: (state) => (name) => {
    const def = codeDefs[name];

    return state[def.name];
  },

  getParameter: (state) => (id) => {
    return state.listParameters.find(x => x.parameterId === id);
  }
};

const actions = {
  setFastFilter({ commit }, filter) {
    commit("setFastFilter", { fastFilter: filter });
  },

  fetchRecords({ commit, state, dispatch }, params) {
    if (state.fetching && params.ignoreFetching === undefined)
      return Promise.resolve();

    const def = defs[params.module];
    const listParams = {
      size: 1000,
      periodFrom: (state.month !== null) ? DateTime.fromFormat(state.month, "yyyy-M").startOf("month").toFormat("yyyy-MM-dd'T00:00:00'") : null,
      periodTo: (state.month !== null) ? DateTime.fromFormat(state.month, "yyyy-M").endOf("month").toFormat("yyyy-MM-dd'T23:59:59'") : null
    };
    const url = (def.listParams)
      ? (def.listUrl || def.url) + def.listParams(listParams)
      : (def.listUrl || def.url);
    let tid;

    const promise = dispatch("progress/add", "Fetch records", { root: true })
      .then(id => {
        tid = id;
        return axios.get(url);
      }).then((response) => {
        commit("recordsFetched", {
          module: params.module,
          records: (def.pageable) ? response.data.content : response.data
        });
        return state.records;
      }).catch((error) => {
        commit("recordsFetched", { module: params.module, records: [] });
        console.log("Objects fetch error [" + params + "]", error);
        return undefined;
      }).finally(() => dispatch("progress/remove", tid, { root: true }));

    // nastavime, ze se objekt s touto URL uz stahuje, aby se to neptalo nekolikrat
    commit("beginFetching");
    return promise;
  },

  fetchRecord({ commit, state, dispatch }, params) {
    // pokud se vozidla právě vyčítaji, nic nedělěj
    if (state.fetching)
      return Promise.resolve();

    if (params.id === undefined) {
      commit("recordFetched", { module: params.module, record: {} });
      return Promise.resolve();
    } else {
      let tid;

      const def = defs[params.module];
      const url = def.url + "/" + params.id + ((def.getParams) ? def.getParams() : "");

      // zavolame stazeni objektu, ktere az se provede, tak se objekt ulozi
      const promise = dispatch("progress/add", "Fetch record", { root: true })
        .then(id => {
          tid = id;
          return axios.get(url);
        })
        .then((response) => {
          commit("recordFetched", { module: params.module, record: response.data });
          return state.record;
        }).catch((error) => {
          commit("recordFetched", { module: params.module, records: null });
          console.log("Object fetch error [" + params + "]", error);
          return undefined;
        }).finally(() => dispatch("progress/remove", tid, { root: true }));


      commit("beginFetching");
      return promise;
    }
  },

  storeRecord({ commit, state, dispatch }, params) {
    if (state.fetching)
      return Promise.resolve();

    const def = defs[params.module];
    let promise;
    let tid;

    if (params.id === undefined) {
      // vytvoreni noveho zaznamu v danem modulu
      promise = dispatch("progress/add", "Fetch record", { root: true })
        .then(id => {
          tid = id;
          return axios.post(def.url, params.object);
        })
        .then(() => {
          return dispatch("fetchRecords", { module: params.module, ignoreFetching: true });
        }).catch((error) => {
          commit("error");
          if (error.response) {
            console.log("Record fetch error", error.response);
            return { error: true, message: error.response.data.error };
          } else
            return { error: true };
        }).finally(() => dispatch("progress/remove", tid, { root: true }));
    } else {
      promise = dispatch("progress/add", "Fetch record", { root: true })
        .then(id => {
          tid = id;
          return axios.put(def.url + "/" + params.id, params.object);
        }).then(() => {
          return dispatch("fetchRecords", { module: params.module, ignoreFetching: true });
        }).catch((error) => {
          commit("error");
          if (error.response) {
            console.log("Record fetch error", error.response);
            if (error.response.status === 412) {
              commit("recordFetched", { module: params.module, record: error.response.data });
              return { error: true, message: i18n.t("error.optimistic_lock") };
            } else
              return { error: true, message: error.response.data.error };
          } else
            return { error: true };
        }).finally(() => dispatch("progress/remove", tid, { root: true }));
    }

    // nastavime, ze se objekt s touto URL uz stahuje, aby se to neptalo nekolikrat
    commit("beginFetching");
    return promise;
  },

  deleteRecord({ commit, state, dispatch }, params) {
    if (state.fetching)
      return Promise.resolve();

    const def = defs[params.module];
    let tid;

    let promise = dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        return axios.delete(def.url + "/" + params.id);
      }).then(() => {
        return dispatch("fetchRecords", { module: params.module, ignoreFetching: true });
      }).catch((error) => {
        commit("error");
        console.log("Record fetch error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));

    commit("beginFetching");
    return promise;
  },

  pushEvent({ state, commit, dispatch }, event) {
    commit("enqueueEvent", event);

    if (!state.activeEvent)
      dispatch("processEvents");
  },

  processEvents({ state, commit }) {
    if (!state.events.length && !state.activeEvent)
      return;

    if (!state.events.length && state.activeEvent) {
      commit("resetEvents");
      return;
    }

    commit("dequeueEvent");
  },

  resetEvents({ state, commit }) {
    if (!state.events.length && !state.activeEvent)
      return;

    commit("resetEvents");
  },

  fetchCodes({ commit, state }, name) {
    const def = codeDefs[name];

    if (def === undefined) {
      console.log("Invalid code name [" + name + "]");
      return undefined;
    }

    if (state[def.name].length > 0)
      return true;

    return axios.get(def.url).then((response) => {
      commit("codesFetched", { name: def.name, data: response.data });
      return true;
    }).catch((error) => {
      console.log("Codes fetch error [" + name + "]", error);
      return undefined;
    });
  },

  // eslint-disable-next-line no-unused-vars
  async fetchAllPageable({ state }, url) {
    const items = [];
    let page = 0;
    let totalPages;

    try {
      do {
        const response = await axios.get(url, {
          params: { page }
        });

        items.push(...response.data.content);
        totalPages = response.data.totalPages;
        page++;
      } while (page < totalPages);

    } catch (error) {
      console.error("Error fetching items:", error);
    }

    return items;
  },

  fetchPersons({ dispatch, rootState }, withgroup = true) {
    let tid;
    const qparams = (withgroup && rootState.groupsets.groupset !== null)
      ? "?groupset=" + rootState.groupsets.groupset.groupSetId : "";

    return dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        return axios.get("/v1/{#company#}/person-brief" + qparams);
      }).catch((error) => {
        console.log("Persons fetch error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  fetchVehicle({ dispatch }, vehicleId) {
    let tid;

    return dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        return axios.get("/v1/{#company#}/vehicle/" + vehicleId);
      }).catch((error) => {
        console.log("Vehicle fetch error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  fetchVehicles({ dispatch, rootState }, withgroup = true) {
    let tid;
    const qparams = (withgroup && rootState.groupsets.groupset !== null)
      ? "?groupset=" + rootState.groupsets.groupset.groupSetId : "";

    return dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        return axios.get("/v1/{#company#}/vehicle-brief" + qparams);
      }).catch((error) => {
        console.log("Vehicles fetch error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  findVehicles({ dispatch }, text) {
    let tid;

    return dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        return axios.get("/v1/{#company#}/find-vehicles?text=" + text);
      }).catch((error) => {
        console.log("Vehicles fetch error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  fetchDaySummaries({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        return axios.get(`/v1/{#company#}/vehicle/${params.vehicleId}/day-summaries?from=${encodeURIComponent(DateTime.fromISO(params.from).toFormat(utils.API_DFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.to).toFormat(utils.API_DFORMAT))}`);
      }).catch((error) => {
        console.log("Day summaries fetch error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  importPerson({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Import person", { root: true })
      .then(id => {
        tid = id;
        return axios.get(`/v1/${params.toCompanyCode}/person/${params.personId}/import/${params.fromCompanyCode}`);
      }).catch((error) => {
        console.log("Import person error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  computeRouteUrl({ dispatch }, url) {
    let tid;
    return dispatch("progress/add", "Compute route from url", { root: true })
      .then(id => {
        tid = id;
        return axios.get(`/v1/{#company#}/map/compute-route-url?url=${url}`);
      }).catch((error) => {
        console.log("Compute route from url", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  importRefueling({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Import refueling", { root: true })
      .then(id => {
        tid = id;

        const formData = new FormData();
        formData.append("file", params.file);

        var url = "/v1/{#company#}/refueling/import";
        if (params.store !== null && !params.store)
          url += "?dryrun=true";

        return axios.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
      }).then((response) => {
        this.dispatch("refueling/refresh");
        return Promise.resolve(response);
      }).catch((error) => {
        console.log("Import person error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  addRefuelingDocument({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Import refueling", { root: true })
      .then(id => {
        tid = id;

        return axios.post(`/v1/{#company#}/refueling/${params.vehicleId}/document/${encodeURIComponent(DateTime.fromISO(params.timestamp).toFormat(utils.API_DTSFORMAT))}`,
          params.document);
      }).then(() => {
        this.dispatch("refueling/refresh");
        return Promise.resolve();
      }).catch((error) => {
        console.log("add refueling document error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  updateRefuelingDocument({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Import refueling", { root: true })
      .then(id => {
        tid = id;

        return axios.put(`/v1/{#company#}/refueling/${params.vehicleId}/document/${encodeURIComponent(DateTime.fromISO(params.timestamp).toFormat(utils.API_DTSFORMAT))}`,
          params.document);
      }).then(() => {
        this.dispatch("refueling/refresh");
        return Promise.resolve();
      }).catch((error) => {
        console.log("update refueling document error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  deleteRefuelingDocument({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Import refueling", { root: true })
      .then(id => {
        tid = id;

        return axios.delete(`/v1/{#company#}/refueling/${params.vehicleId}/document/${encodeURIComponent(DateTime.fromISO(params.timestamp).toFormat(utils.API_DTSFORMAT))}`,
          params.document);
      }).then(() => {
        this.dispatch("refueling/refresh");
        return Promise.resolve();
      }).catch((error) => {
        console.log("delete refueling document error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  updateRide({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Update ride", { root: true })
      .then(id => {
        tid = id;

        return axios.patch(`/v1/{#company#}/vehicle/${params.vehicleId}/ride/${encodeURIComponent(DateTime.fromISO(params.rideStart).toFormat(utils.API_DTSFORMAT))}`,
          params.body);
      }).catch((error) => {
        console.log("Import update ride error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  updateVehicle({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Update vehicle parameters", { root: true })
      .then(id => {
        tid = id;

        return axios.patch(`/v1/{#company#}/vehicle-params/${params.vehicleId}`, params.body);
      }).catch((error) => {
        console.log("Update vehicle parameters error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  searchSubject({ dispatch }, params) {
    let tid;

    return dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        return axios.get("/v1/{#company#}/subject-find-id/" + params.countryCode + "/" + params.id);
      }).catch((error) => {
        console.log("Subject search error", error);
        if (error.response)
          return { error: true, status: error.response.status, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  fetchSubjects({ dispatch }) {
    let tid;

    return dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        return axios.get("/v1/{#company#}/subject?size=2000");
      }).catch((error) => {
        console.log("Subjects fetch error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  fetchContracts({ dispatch }, params) {
    let tid;

    return dispatch("progress/add", "Fetch record", { root: true })
      .then(id => {
        tid = id;
        const qparams = (params.groupset !== null) ? "&groupset=" + params.groupset.groupSetId : "";
        return axios.get(`/v1/{#company#}/contract?from=${encodeURIComponent(DateTime.fromISO(params.from).toFormat(utils.API_DTFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.to).toFormat(utils.API_DTFORMAT))}${qparams}&size=5000`);
      }).catch((error) => {
        console.log("Contracts fetch error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  resetFlag({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Reset flag", { root: true })
      .then(id => {
        tid = id;

        return axios.put(`/v1/{#company#}/vehicle/${params.vehicleId}/reset-flag/${params.flag}`);
      }).catch((error) => {
        console.log("Reset flag error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => {
        dispatch("online/fetchVehicles", null, { root: true });
        dispatch("progress/remove", tid, { root: true });
      });
  },

  /**
   * Z úpravy jednoho záznmu zahájí upravu nebo zadání nového.
   *
   * @param params - {
   *   pushModule: "...",
   *   pushObject: obj
   *   module: "..."
   *   id: "..."
   * }
   */
  recordPush({ state }, params) {
    state.stack.push(params.pushObject);
    params.router.push({ name: "data/" + params.module, params: { id: params.id } });
  },

  /**
   * Po ukončení uúpravy záznamu se vrací na předchozí záznam, nebo na zadané url.
   * @param params - {
   *   router: obj,
   *   route: obj
   * }
   */
  recordPop({ commit, state }, params) {
    if (state.stack.length > 0) {
      const last = state.stack.pop();
      if (last.module === params.module) {
        commit("recordFetched", { module: last.module, record: last.object });
        return true;
      }
    }
    return false;
  }

};

const mutations = {
  recordStored(state, params) {
    Vue.set(state, "module", params.module);
    Vue.set(state, "record", params.record);
    Vue.set(state, "records", null);

    Vue.set(state, "fetching", false);
    Vue.set(state, "fetched", true);
  },

  recordFetched(state, params) {
    Vue.set(state, "module", params.module);
    Vue.set(state, "def", defs[params.module]);
    Vue.set(state, "record", params.record);
    Vue.set(state, "records", null);

    Vue.set(state, "fetching", false);
    Vue.set(state, "fetched", true);
  },

  recordsFetched(state, params) {
    Vue.set(state, "module", params.module);
    Vue.set(state, "def", defs[params.module]);
    Vue.set(state, "record", null);
    Vue.set(state, "records", params.records);

    Vue.set(state, "fetching", false);
    Vue.set(state, "fetched", true);
  },

  error(state) {
    Vue.set(state, "fetching", false);
    Vue.set(state, "fetched", true);
  },

  beginFetching(state) {
    Vue.set(state, "fetching", true);
  },

  setFastFilter(state, params) {
    Vue.set(state, "fastFilter", params.fastFilter);
  },

  resetEvents(state) {
    Vue.set(state, "events", []);
    Vue.set(state, "activeEvent", null);
  },

  enqueueEvent(state, event) {
    const events = [...state.events, event];

    Vue.set(state, "events", events);
  },

  dequeueEvent(state) {
    const [active, ...queue] = state.events;

    state.events = queue;
    Vue.set(state, "activeEvent", active);
  },

  codesFetched(state, params) {
    Vue.set(state, params.name, params.data);
  }

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};